/**
 * Tailwind/CSS-class implementation of AccordionContent.
 * Uses the Seeds accordion CSS classes from accordion.css.
 */

import React, { useContext } from "react";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { AccordionContext } from "./Accordion";
import { type TypeAccordionSystemProps } from "./AccordionTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

interface TypeAccordionContentTailwindProps extends TypeAccordionSystemProps {
  children?: React.ReactNode;
}

export const AccordionContentTailwind = ({
  children,
  className,
  ...rest
}: TypeAccordionContentTailwindProps) => {
  const { styled } = useContext(AccordionContext);

  // data-styled is forwarded to the Radix Content element via spread: React
  // component prop types have no data-* index signature, so spreading (which is
  // not subject to excess-property checks) keeps this type-safe. Radix forwards
  // the attribute to the underlying DOM node so the CSS [data-styled] rules fire.
  const contentDataAttrs = { "data-styled": styled };

  return (
    <RadixAccordion.Content
      className={cn("seeds-accordion-content", className)}
      {...contentDataAttrs}
    >
      <div
        className="seeds-accordion-content-inner"
        data-styled={styled}
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      >
        {children}
      </div>
    </RadixAccordion.Content>
  );
};
